import { Flex, Badge, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy } from "@tabler/icons-react";

/**
 * Caller-supplied tags and reference ids.
 *
 * These are data, not status, so they render in the tone of data: Mantine badges uppercase their
 * content by default, which destroyed the casing of values people match on exactly, and the solid
 * fill made arbitrary strings look like state. Clicking has always copied — the behaviour was
 * fine, the affordance was a `title` attribute nobody hovers long enough to see, and it gave no
 * confirmation. Now it says what it does and tells you when it's done.
 */
export default function TagGroup({
  tags,
  vertical,
  size = "md",
  justify = "flex-start",
  color,
}) {
  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value);
    notifications.show({
      message: `Copied “${value}”`,
      color: "teal",
      autoClose: 1500,
    });
  };

  return (
    tags && (
      <Flex
        gap={size}
        direction={vertical ? "column" : "row"}
        justify={justify}
        wrap="wrap"
      >
        {tags.map((p, idx) => (
          <Tooltip
            key={"tag" + p + idx}
            label="Click to copy"
            withArrow
            openDelay={300}
          >
            <Badge
              color={color || "gray"}
              size={size}
              // An explicit colour is a caller distinguishing one family of values from another
              // (reference ids from tags); outline keeps that legible without going solid.
              variant={color ? "outline" : "default"}
              tt="none"
              radius="sm"
              rightSection={<IconCopy size={11} style={{ opacity: 0.55 }} />}
              style={{
                maxWidth: "100%",
                textOverflow: "unset",
                whiteSpace: "normal",
                wordBreak: "break-all",
                height: "auto",
                fontFamily: "var(--mantine-font-family-monospace)",
                fontWeight: 400,
                cursor: "pointer",
              }}
              onClick={() => copyToClipboard(p)}
            >
              {p}
            </Badge>
          </Tooltip>
        ))}
      </Flex>
    )
  );
}
